import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { ExcelExportIconButton, PdfExportIconButton } from '../components/common/ExportFormatIconButtons';
import { get } from '../utils/api';
import {
  exportKotilingaTreeRevenueExcel,
  exportKotilingaTreeRevenuePdf,
} from '../utils/kotilingaTreeRevenueExport';
import {
  exportKotilingaTreeRevenueSponsorsExcel,
  exportKotilingaTreeRevenueSponsorsPdf,
} from '../utils/kotilingaTreeRevenueSponsorsExport';
import {
  collectDescendantProducts,
  dedupeProductsById,
  revenueTableRowHoverSx,
  revenueTreeRowSx,
  sortCategoriesByName,
  sortProductsByName,
} from '../utils/kotilingaTreeReportSort';

function fmtCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(Number(value || 0)));
}

function fmtPercent(value) {
  const n = Number(value || 0);
  return `${n.toFixed(1)}%`;
}

function decodeHtmlEntities(value) {
  return String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// Product names are stored with their subcategory prepended (e.g. "Adithya Lingas - Bhaga Lingam").
// When a product row is shown nested under that subcategory's header, drop the redundant prefix so it
// reads just "Bhaga Lingam". Only strips an exact leading "{parent} <sep> " match; otherwise unchanged.
function stripParentPrefix(name, parentName) {
  const parent = String(parentName || '').trim();
  if (!parent) return name;
  const escaped = parent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const stripped = name.replace(new RegExp(`^${escaped}\\s*[-–—:·]\\s*`, 'i'), '').trim();
  return stripped || name;
}

function formatProductRowLabel(row) {
  const name = stripParentPrefix(decodeHtmlEntities(row.name), row.parentName);
  if (row.type !== 'product') return name;
  const price = Number(row.price_num);
  if (!Number.isFinite(price) || price <= 0) return name;
  return `${name} · ${fmtCurrency(price)}`;
}

const PLEDGE_NA = '—';
const pledgeNaSx = { color: 'text.disabled' };

/** Sponsor/product has a pledge on this row when pledge amount is positive. */
function hasPledgeMetrics(metrics = {}) {
  return Number(metrics.pledge_amount || 0) > 0;
}

/** Non-pledge-only sponsor (no pledge on this row). */
function isGeneralDonationSponsor(row = {}) {
  return !hasPledgeMetrics(row) && Number(row.non_pledge_donations || 0) > 0;
}

/** True when non-pledge amount would display as a non-zero dollar value. */
function hasNonPledgeDonations(metrics = {}) {
  const n = Number(metrics.non_pledge_donations || 0);
  if (!Number.isFinite(n)) return false;
  return Math.round(n) !== 0;
}

function NonPledgeDonationCell({ metrics, align = 'right', bold = false }) {
  if (!hasNonPledgeDonations(metrics)) {
    return (
      <TableCell align={align} sx={{ ...(bold ? { fontWeight: 700 } : {}), ...pledgeNaSx }}>
        {PLEDGE_NA}
      </TableCell>
    );
  }
  return (
    <TableCell align={align} sx={bold ? { fontWeight: 700 } : undefined}>
      {fmtCurrency(metrics.non_pledge_donations)}
    </TableCell>
  );
}

function PledgeMetricCell({ applicable, children, align = 'right' }) {
  if (!applicable) {
    return (
      <TableCell align={align} sx={pledgeNaSx}>
        {PLEDGE_NA}
      </TableCell>
    );
  }
  return <TableCell align={align}>{children}</TableCell>;
}

function pledgeBalanceAmount(metrics = {}) {
  if (metrics.pledge_balance != null && metrics.pledge_balance !== '') {
    return Number(metrics.pledge_balance) || 0;
  }
  return Number(metrics.pledge_amount || 0) - Number(metrics.paid_amount || 0);
}

function hasNonZeroPledgeBalance(metrics = {}) {
  if (!hasPledgeMetrics(metrics)) return false;
  return Math.round(pledgeBalanceAmount(metrics)) !== 0;
}

function sponsorHasNonZeroPledgeBalance(sponsor = {}) {
  if (!hasPledgeMetrics(sponsor)) return false;
  return Math.round(sponsorPledgeBalance(sponsor)) !== 0;
}

function PledgeBalanceCell({ metrics, dialogNode, onOpenBalance, bold = false }) {
  if (!hasPledgeMetrics(metrics)) {
    return (
      <TableCell align="right" sx={{ ...(bold ? { fontWeight: 700 } : {}), ...pledgeNaSx }}>
        {PLEDGE_NA}
      </TableCell>
    );
  }
  const label = fmtCurrency(pledgeBalanceAmount(metrics));
  if (!hasNonZeroPledgeBalance(metrics) || !onOpenBalance || !dialogNode) {
    return (
      <TableCell align="right" sx={bold ? { fontWeight: 700 } : undefined}>
        {label}
      </TableCell>
    );
  }
  return (
    <TableCell align="right" sx={bold ? { fontWeight: 700 } : undefined}>
      <Button
        variant="text"
        size="small"
        onClick={() => onOpenBalance(dialogNode, { balanceOnly: true })}
        sx={{
          fontWeight: bold ? 700 : 500,
          minWidth: 0,
          px: 1.25,
          py: 0.35,
          textTransform: 'none',
          color: 'primary.dark',
          bgcolor: 'rgba(25, 118, 210, 0.12)',
          border: '1px solid',
          borderColor: 'rgba(25, 118, 210, 0.35)',
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'rgba(25, 118, 210, 0.2)',
            borderColor: 'primary.main',
          },
        }}
      >
        {label}
      </Button>
    </TableCell>
  );
}

function isPledgeStatus(status) {
  const s = String(status || '').toLowerCase();
  return s.includes('pledge');
}

function fmtTxDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

const SPONSORS_PAGE_SIZE = 50;

/** Sort arrows always visible (MUI hides inactive icons until hover by default). */
const sponsorSortLabelSx = {
  '& .MuiTableSortLabel-icon': {
    opacity: 0.65,
    visibility: 'visible',
  },
  '&.Mui-active .MuiTableSortLabel-icon': {
    opacity: 1,
  },
};

function sponsorPledgeBalance(row = {}) {
  if (row.pledge_balance != null && row.pledge_balance !== '') {
    return Number(row.pledge_balance) || 0;
  }
  return Number(row.pledge_amount || 0) - Number(row.paid_amount || 0);
}

function compareSponsorRows(a, b, field, direction) {
  const mul = direction === 'desc' ? -1 : 1;
  if (field === 'sponsor_name') {
    return (
      mul
      * String(a.sponsor_name || 'Guest').localeCompare(String(b.sponsor_name || 'Guest'), undefined, {
        sensitivity: 'base',
      })
    );
  }
  if (field === 'pledge_amount') {
    return mul * (Number(a.pledge_amount || 0) - Number(b.pledge_amount || 0));
  }
  if (field === 'pledge_balance') {
    return mul * (sponsorPledgeBalance(a) - sponsorPledgeBalance(b));
  }
  return 0;
}

/** Category ids with nested subcategories (default expanded; products stay collapsed). */
function collectBranchCategoryIds(nodes = [], acc = []) {
  nodes.forEach((node) => {
    const children = Array.isArray(node.children) ? node.children : [];
    if (children.length > 0) {
      acc.push(node.id);
      collectBranchCategoryIds(children, acc);
    }
  });
  return acc;
}

function flattenRows(nodes = [], expanded = new Set(), showProducts = new Set(), depth = 0) {
  const rows = [];
  sortCategoriesByName(nodes).forEach((node) => {
    const childCategories = sortCategoriesByName(Array.isArray(node.children) ? node.children : []);
    const descendantProducts = collectDescendantProducts(node);
    const hasChildCategories = childCategories.length > 0;
    const hasProducts = descendantProducts.length > 0;
    const hasChildren = hasChildCategories || hasProducts;
    const isExpanded = expanded.has(node.id);
    const productsExpanded = showProducts.has(node.id);

    rows.push({
      key: `cat-${node.id}`,
      type: 'category',
      depth,
      id: node.id,
      name: node.name,
      hasChildren,
      hasChildCategories,
      hasProducts,
      expanded: isExpanded,
      productsExpanded,
      metrics: node.metrics || {},
    });

    if (!isExpanded && !productsExpanded) return;

    if (productsExpanded) {
      sortProductsByName(dedupeProductsById(descendantProducts)).forEach((product) => {
        rows.push({
          key: `prod-${product.id}-${node.id}`,
          type: 'product',
          depth: depth + 1,
          id: product.id,
          name: product.name,
          parentName: node.name,
          price_num: product.price_num,
          hasChildren: false,
          expanded: false,
          metrics: product.metrics || {},
        });
      });
      return;
    }

    if (isExpanded) {
      childCategories.forEach((child) => {
        rows.push(...flattenRows([child], expanded, showProducts, depth + 1));
      });
    }
  });
  return rows;
}

// Space/punctuation-insensitive so "Srirudra" matches "Sri Rudra", "mahalakshmi" matches "Maha Lakshmi", etc.
const normalizeSearch = (s) => decodeHtmlEntities(String(s || '')).toLowerCase().replace(/[^a-z0-9]+/g, '');

function flattenProductSearchRows(nodes = [], query = '', depth = 0) {
  const q = normalizeSearch(query);
  const walk = (node, level) => {
    const children = sortCategoriesByName(Array.isArray(node.children) ? node.children : []);
    const products = sortProductsByName(Array.isArray(node.products) ? node.products : []);

    const childRows = [];
    children.forEach((child) => {
      childRows.push(...walk(child, level + 1));
    });

    const matchingProducts = products.filter((product) => (
      normalizeSearch(product.name).includes(q)
    ));

    if (childRows.length === 0 && matchingProducts.length === 0) return [];

    const categoryRow = {
      key: `cat-${node.id}`,
      type: 'category',
      depth: level,
      id: node.id,
      name: node.name,
      hasChildren: true,
      expanded: true,
      metrics: node.metrics || {},
    };
    const productRows = matchingProducts.map((product) => ({
      key: `prod-${product.id}-${node.id}`,
      type: 'product',
      depth: level + 1,
      id: product.id,
      name: product.name,
      price_num: product.price_num,
      hasChildren: false,
      expanded: false,
      metrics: product.metrics || {},
    }));
    return [categoryRow, ...childRows, ...productRows];
  };

  return sortCategoriesByName(nodes).flatMap((node) => walk(node, depth));
}

function flattenAvailableRows(nodes = [], depth = 0) {
  const rows = [];

  const walk = (node, level) => {
    const children = sortCategoriesByName(Array.isArray(node.children) ? node.children : []);
    const products = sortProductsByName(Array.isArray(node.products) ? node.products : []);

    const childRows = [];
    children.forEach((child) => {
      childRows.push(...walk(child, level + 1));
    });

    const availableProducts = products.filter(
      (product) => Number(product?.metrics?.unique_sponsors || 0) === 0
    );

    const hasMatches = childRows.length > 0 || availableProducts.length > 0;
    if (!hasMatches) return [];

    const categoryRow = {
      key: `cat-${node.id}`,
      type: 'category',
      depth: level,
      id: node.id,
      name: node.name,
      hasChildren: true,
      expanded: true,
      metrics: node.metrics || {},
    };

    const productRows = availableProducts.map((product) => ({
      key: `prod-${product.id}-${node.id}`,
      type: 'product',
      depth: level + 1,
      id: product.id,
      name: product.name,
      price_num: product.price_num,
      hasChildren: false,
      expanded: false,
      metrics: product.metrics || {},
    }));

    return [categoryRow, ...childRows, ...productRows];
  };

  sortCategoriesByName(nodes).forEach((node) => {
    rows.push(...walk(node, depth));
  });

  return rows;
}

function sumRoots(roots = []) {
  return roots.reduce((acc, r) => {
    const m = r?.metrics || {};
    acc.unique_sponsors += Number(m.unique_sponsors || 0);
    acc.non_pledge_donations += Number(m.non_pledge_donations || 0);
    acc.pledge_amount += Number(m.pledge_amount || 0);
    acc.paid_amount += Number(m.paid_amount || 0);
    return acc;
  }, { unique_sponsors: 0, non_pledge_donations: 0, pledge_amount: 0, paid_amount: 0 });
}

export default function KotilingaTreeRevenueReportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [expanded, setExpanded] = React.useState(new Set());
  const [showProducts, setShowProducts] = React.useState(new Set());
  const [meta, setMeta] = React.useState({ total_categories: 0, total_products: 0 });
  const [exporting, setExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState('');
  const [sponsorsOpen, setSponsorsOpen] = React.useState(false);
  const [sponsorsLoading, setSponsorsLoading] = React.useState(false);
  const [sponsorsError, setSponsorsError] = React.useState('');
  const [sponsorsRows, setSponsorsRows] = React.useState([]);
  const [sponsorsTitle, setSponsorsTitle] = React.useState('');
  const [sponsorsNode, setSponsorsNode] = React.useState({ type: 'all', id: null });
  const [sponsorsSearch, setSponsorsSearch] = React.useState('');
  const [sponsorsSort, setSponsorsSort] = React.useState({ field: 'sponsor_name', direction: 'asc' });
  const [sponsorsPage, setSponsorsPage] = React.useState(0);
  const [expandedSponsorKey, setExpandedSponsorKey] = React.useState('');
  const [txLoading, setTxLoading] = React.useState(false);
  const [txRows, setTxRows] = React.useState([]);
  const [txError, setTxError] = React.useState('');
  const [showAvailableOnly, setShowAvailableOnly] = React.useState(false);
  const [treeSearchMode, setTreeSearchMode] = React.useState('product');
  const [treeSearchQuery, setTreeSearchQuery] = React.useState('');
  const [donorSearchRows, setDonorSearchRows] = React.useState([]);
  const [donorSearchLoading, setDonorSearchLoading] = React.useState(false);
  const [donorSearchError, setDonorSearchError] = React.useState('');
  const [sponsorsBalanceOnly, setSponsorsBalanceOnly] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/ldb/kotilinga-reports/tree-revenue-pledges');
      const tree = Array.isArray(res?.data) ? res.data : [];
      setRows(tree);
      setMeta(res?.meta || { total_categories: 0, total_products: 0 });
      setExpanded(new Set(collectBranchCategoryIds(tree)));
      setShowProducts(new Set());
    } catch (e) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const treeSearchTrimmed = treeSearchQuery.trim();
  const isProductSearchActive = treeSearchMode === 'product' && treeSearchTrimmed.length > 0;
  const isDonorSearchActive = treeSearchMode === 'donor' && treeSearchTrimmed.length >= 2;

  React.useEffect(() => {
    if (treeSearchMode !== 'donor') {
      setDonorSearchRows([]);
      setDonorSearchError('');
      setDonorSearchLoading(false);
      return undefined;
    }
    if (treeSearchTrimmed.length < 2) {
      setDonorSearchRows([]);
      setDonorSearchError('');
      setDonorSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setDonorSearchLoading(true);
      setDonorSearchError('');
      try {
        const res = await get('/ldb/kotilinga-reports/tree-revenue-pledges/donor-search', {
          q: treeSearchTrimmed,
        });
        if (!cancelled) {
          setDonorSearchRows(Array.isArray(res?.data) ? res.data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setDonorSearchError(e?.message || 'Donor search failed');
          setDonorSearchRows([]);
        }
      } finally {
        if (!cancelled) setDonorSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [treeSearchMode, treeSearchTrimmed]);

  const visibleRows = React.useMemo(() => {
    if (isDonorSearchActive) return [];
    if (showAvailableOnly) return flattenAvailableRows(rows);
    if (isProductSearchActive) return flattenProductSearchRows(rows, treeSearchTrimmed);
    return flattenRows(rows, expanded, showProducts);
  }, [rows, expanded, showProducts, showAvailableOnly, isProductSearchActive, isDonorSearchActive, treeSearchTrimmed]);

  const productSearchStats = React.useMemo(() => {
    if (!isProductSearchActive) return null;
    const matched = flattenProductSearchRows(rows, treeSearchTrimmed);
    return {
      products: matched.filter((r) => r.type === 'product').length,
      categories: matched.filter((r) => r.type === 'category').length,
    };
  }, [rows, isProductSearchActive, treeSearchTrimmed]);

  const totals = React.useMemo(() => {
    const base = sumRoots(rows);
    const pledgeBalance = base.pledge_amount - base.paid_amount;
    const totalRevenue = base.non_pledge_donations + base.paid_amount;
    const pctPledgePaid = base.pledge_amount > 0 ? (base.paid_amount / base.pledge_amount) * 100 : 0;
    return { ...base, pledge_balance: pledgeBalance, total_revenue: totalRevenue, pct_pledge_paid: pctPledgePaid };
  }, [rows]);

  const toggleExpand = (row) => {
    const id = row.id;
    if (row.hasChildCategories && row.hasProducts) {
      if (row.expanded && !row.productsExpanded) {
        setShowProducts((prev) => {
          const next = new Set(prev);
          next.add(id);
          return next;
        });
        return;
      }
      if (row.productsExpanded) {
        setShowProducts((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        return;
      }
    }
    if (row.hasChildCategories) {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          setShowProducts((sp) => {
            const n = new Set(sp);
            n.delete(id);
            return n;
          });
        } else {
          next.add(id);
        }
        return next;
      });
      return;
    }
    if (row.hasProducts) {
      setShowProducts((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const sponsorsFilteredSorted = React.useMemo(() => {
    const q = sponsorsSearch.trim().toLowerCase();
    let list = sponsorsRows;
    if (sponsorsBalanceOnly) {
      list = list.filter((s) => sponsorHasNonZeroPledgeBalance(s));
    }
    if (q) {
      list = list.filter((s) => String(s.sponsor_name || 'Guest').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => compareSponsorRows(a, b, sponsorsSort.field, sponsorsSort.direction));
  }, [sponsorsRows, sponsorsSearch, sponsorsSort, sponsorsBalanceOnly]);

  const sponsorsUsePagination = sponsorsFilteredSorted.length > SPONSORS_PAGE_SIZE;

  const sponsorsPageRows = React.useMemo(() => {
    if (!sponsorsUsePagination) return sponsorsFilteredSorted;
    const start = sponsorsPage * SPONSORS_PAGE_SIZE;
    return sponsorsFilteredSorted.slice(start, start + SPONSORS_PAGE_SIZE);
  }, [sponsorsFilteredSorted, sponsorsPage, sponsorsUsePagination]);

  const sponsorsTablePagination = sponsorsUsePagination ? (
    <TablePagination
      component="div"
      count={sponsorsFilteredSorted.length}
      page={sponsorsPage}
      onPageChange={(_, nextPage) => setSponsorsPage(nextPage)}
      rowsPerPage={SPONSORS_PAGE_SIZE}
      rowsPerPageOptions={[SPONSORS_PAGE_SIZE]}
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
    />
  ) : null;

  React.useEffect(() => {
    if (!expandedSponsorKey) return;
    if (!sponsorsFilteredSorted.some((s) => s.sponsor_key === expandedSponsorKey)) {
      setExpandedSponsorKey('');
      setTxRows([]);
      setTxError('');
    }
  }, [sponsorsFilteredSorted, expandedSponsorKey]);

  const handleSponsorsSort = (field) => {
    setSponsorsSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return {
        field,
        direction: field === 'sponsor_name' ? 'asc' : 'desc',
      };
    });
    setSponsorsPage(0);
  };

  const exportSponsorsDialog = (format) => {
    const payload = { title: sponsorsTitle, rows: sponsorsFilteredSorted };
    if (format === 'pdf') exportKotilingaTreeRevenueSponsorsPdf(payload);
    else exportKotilingaTreeRevenueSponsorsExcel(payload);
  };

  const openSponsorsDialog = async (node, options = {}) => {
    const balanceOnly = Boolean(options.balanceOnly);
    setSponsorsOpen(true);
    setSponsorsLoading(true);
    setSponsorsError('');
    setSponsorsRows([]);
    setSponsorsSearch(String(options.filterName || ''));
    setSponsorsBalanceOnly(balanceOnly);
    setSponsorsSort({ field: 'sponsor_name', direction: 'asc' });
    setSponsorsPage(0);

    const params = {};
    const titleSuffix = balanceOnly ? ' — Pledge balance' : '';

    if (!node || node.type === 'all') {
      params.node_type = 'all';
      setSponsorsTitle(`Sponsors - Total${titleSuffix}`);
      setSponsorsNode({ type: 'all', id: null });
    } else if (node.type === 'category') {
      params.node_type = 'category';
      params.node_id = node.id;
      setSponsorsTitle(`Sponsors - ${node.name}${titleSuffix}`);
      setSponsorsNode({ type: 'category', id: node.id });
    } else {
      params.node_type = 'product';
      params.node_id = node.id;
      setSponsorsTitle(`Sponsors - ${node.name}${titleSuffix}`);
      setSponsorsNode({ type: 'product', id: node.id });
    }
    setExpandedSponsorKey('');
    setTxRows([]);
    setTxError('');

    try {
      const res = await get('/ldb/kotilinga-reports/tree-revenue-pledges/sponsors', params);
      setSponsorsRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setSponsorsError(e?.message || 'Failed to load sponsors');
    } finally {
      setSponsorsLoading(false);
    }
  };

  const toggleSponsorTx = async (sponsor) => {
    const key = String(sponsor?.sponsor_key || '');
    if (!key) return;
    if (expandedSponsorKey === key) {
      setExpandedSponsorKey('');
      setTxRows([]);
      setTxError('');
      return;
    }

    setExpandedSponsorKey(key);
    setTxRows([]);
    setTxError('');
    setTxLoading(true);
    try {
      const params = {
        node_type: sponsorsNode.type,
        sponsor_key: key,
      };
      if (sponsorsNode.id) params.node_id = sponsorsNode.id;
      const res = await get('/ldb/kotilinga-reports/tree-revenue-pledges/sponsor-transactions', params);
      setTxRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setTxError(e?.message || 'Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  };

  const runExport = async (format) => {
    setExporting(true);
    setExportError('');
    try {
      const data = await get('/ldb/kotilinga-reports/tree-revenue-pledges/export');
      if (format === 'pdf') exportKotilingaTreeRevenuePdf(data);
      else exportKotilingaTreeRevenueExcel(data);
    } catch (e) {
      setExportError(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <IconButton onClick={() => navigate('/kotilinga/sponsorships')} size="small" title="Kotilinga dashboard">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Revenue Report</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            All products in the Kadambavanam category tree and all-time order totals. Export includes product summaries and donor totals per product.
          </Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.5} sx={{ width: '100%' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" sx={{ width: '100%' }}>
          <ToggleButtonGroup
            value={treeSearchMode}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (!value) return;
              setTreeSearchMode(value);
              setTreeSearchQuery('');
              setDonorSearchRows([]);
              setDonorSearchError('');
              setShowAvailableOnly(false);
            }}
            disabled={showAvailableOnly}
          >
            <ToggleButton value="product">Product</ToggleButton>
            <ToggleButton value="donor">Donor</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label={treeSearchMode === 'product' ? 'Search product' : 'Search donor'}
            placeholder={
              treeSearchMode === 'product'
                ? 'Search product name…'
                : 'Search donor name (min 2 characters)…'
            }
            value={treeSearchQuery}
            onChange={(e) => setTreeSearchQuery(e.target.value)}
            disabled={showAvailableOnly}
            sx={{ minWidth: { xs: '100%', md: 280 }, flex: { md: 1 }, maxWidth: { md: 480 } }}
            InputProps={{
              endAdornment: treeSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setTreeSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            variant={showAvailableOnly ? 'contained' : 'outlined'}
            onClick={() => {
              setShowAvailableOnly((prev) => {
                const next = !prev;
                if (next) {
                  setTreeSearchQuery('');
                  setDonorSearchRows([]);
                  setDonorSearchError('');
                }
                return next;
              });
            }}
            disabled={loading || exporting || isProductSearchActive || isDonorSearchActive}
          >
            {showAvailableOnly ? 'Show All' : 'Show Available'}
          </Button>
          <ExcelExportIconButton
            disabled={loading || exporting}
            onClick={() => runExport('excel')}
          />
          <PdfExportIconButton
            disabled={loading || exporting}
            loading={exporting}
            onClick={() => runExport('pdf')}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Categories: {Number(meta.total_categories || 0).toLocaleString()} · Products: {Number(meta.total_products || 0).toLocaleString()}
        </Typography>
        {isProductSearchActive && productSearchStats && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {productSearchStats.products === 0
              ? `No products match "${treeSearchTrimmed}".`
              : `${productSearchStats.products.toLocaleString()} product${productSearchStats.products === 1 ? '' : 's'} in ${productSearchStats.categories.toLocaleString()} categor${productSearchStats.categories === 1 ? 'y' : 'ies'}`}
          </Typography>
        )}
        {treeSearchMode === 'donor' && treeSearchTrimmed.length > 0 && treeSearchTrimmed.length < 2 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Type at least 2 characters to search donors.
          </Typography>
        )}
        {isDonorSearchActive && !donorSearchLoading && !donorSearchError && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {donorSearchRows.length === 0
              ? `No donors match "${treeSearchTrimmed}".`
              : `${donorSearchRows.length.toLocaleString()} match${donorSearchRows.length === 1 ? '' : 'es'}`}
          </Typography>
        )}
        {showAvailableOnly && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Showing products with zero sponsors only. Matching category branches are auto-expanded.
          </Typography>
        )}
        </Stack>
      </Paper>

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError('')}>
          {exportError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 360 }}>Category / Product</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Products</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Unique sponsors</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Non-pledge donations</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Pledge amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Pledge balance</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total revenue</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>% Pledge Paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'action.hover', ...revenueTableRowHoverSx }}>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {Number(meta.total_products || 0).toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {totals.unique_sponsors > 0 ? (
                    <Button variant="text" size="small" onClick={() => openSponsorsDialog({ type: 'all' })}>
                      {totals.unique_sponsors.toLocaleString()}
                    </Button>
                  ) : '0'}
                </TableCell>
                <NonPledgeDonationCell metrics={totals} bold />
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(totals.pledge_amount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(totals.paid_amount)}</TableCell>
                <PledgeBalanceCell
                  metrics={totals}
                  dialogNode={{ type: 'all' }}
                  onOpenBalance={openSponsorsDialog}
                  bold
                />
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(totals.total_revenue)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtPercent(totals.pct_pledge_paid)}</TableCell>
              </TableRow>
              {visibleRows.length === 0 && isProductSearchActive && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No products match your search.
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((row) => (
                <TableRow
                  key={row.key}
                  sx={revenueTreeRowSx(row)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', pl: row.depth * 2 }}>
                      {row.type === 'category' && row.hasChildren ? (
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(row)}
                          sx={{ mr: 0.5 }}
                          disabled={showAvailableOnly || isProductSearchActive}
                        >
                          {(row.expanded || row.productsExpanded) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 30, mr: 0.5 }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: row.type === 'category' ? 700 : 400 }}>
                        {formatProductRowLabel(row)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={row.type === 'product' ? pledgeNaSx : undefined}>
                    {row.type === 'category'
                      ? Number(row.metrics.product_count || 0).toLocaleString()
                      : PLEDGE_NA}
                  </TableCell>
                  <TableCell align="right">
                    {Number(row.metrics.unique_sponsors || 0) > 0 ? (
                      <Button variant="text" size="small" onClick={() => openSponsorsDialog(row)}>
                        {Number(row.metrics.unique_sponsors || 0).toLocaleString()}
                      </Button>
                    ) : '0'}
                  </TableCell>
                  <NonPledgeDonationCell metrics={row.metrics} />
                  <PledgeMetricCell applicable={hasPledgeMetrics(row.metrics)}>
                    {fmtCurrency(row.metrics.pledge_amount)}
                  </PledgeMetricCell>
                  <PledgeMetricCell applicable={hasPledgeMetrics(row.metrics)}>
                    {fmtCurrency(row.metrics.paid_amount)}
                  </PledgeMetricCell>
                  <PledgeBalanceCell
                    metrics={row.metrics}
                    dialogNode={row}
                    onOpenBalance={openSponsorsDialog}
                  />
                  <TableCell align="right">{fmtCurrency(row.metrics.total_revenue)}</TableCell>
                  <PledgeMetricCell applicable={hasPledgeMetrics(row.metrics)}>
                    {fmtPercent(row.metrics.pct_pledge_paid)}
                  </PledgeMetricCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={sponsorsOpen} onClose={() => setSponsorsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pr: 7 }}>
          {sponsorsTitle}
          <IconButton
            onClick={() => setSponsorsOpen(false)}
            size="small"
            aria-label="Close sponsors dialog"
            sx={{
              position: 'absolute',
              right: 12,
              top: 10,
              width: 24,
              height: 24,
              bgcolor: 'error.main',
              color: 'common.white',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {sponsorsError && (
            <Alert severity="error" sx={{ mb: 2 }}>{sponsorsError}</Alert>
          )}
          {sponsorsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap" sx={{ mb: 1.5 }}>
                <TextField
                  size="small"
                  label="Search sponsor name"
                  placeholder="Search all sponsors in this list…"
                  value={sponsorsSearch}
                  onChange={(e) => {
                    setSponsorsSearch(e.target.value);
                    setSponsorsPage(0);
                  }}
                  sx={{ minWidth: { sm: 280 }, flex: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {sponsorsSearch.trim()
                    ? `${sponsorsFilteredSorted.length.toLocaleString()} of ${sponsorsRows.length.toLocaleString()} sponsors`
                    : `${sponsorsRows.length.toLocaleString()} sponsors`}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartIcon />}
                    disabled={sponsorsFilteredSorted.length === 0}
                    onClick={() => exportSponsorsDialog('excel')}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    disabled={sponsorsFilteredSorted.length === 0}
                    onClick={() => exportSponsorsDialog('pdf')}
                  >
                    PDF
                  </Button>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                * General donation
              </Typography>
              {sponsorsBalanceOnly && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Showing sponsors with a non-zero pledge balance (owed or overpaid).
                </Typography>
              )}
              {sponsorsTablePagination}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 60 }} />
                      <TableCell sx={{ fontWeight: 700 }} sortDirection={sponsorsSort.field === 'sponsor_name' ? sponsorsSort.direction : false}>
                        <TableSortLabel
                          active={sponsorsSort.field === 'sponsor_name'}
                          direction={sponsorsSort.field === 'sponsor_name' ? sponsorsSort.direction : 'asc'}
                          onClick={() => handleSponsorsSort('sponsor_name')}
                          sx={sponsorSortLabelSx}
                        >
                          Sponsor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Orders</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Non-pledge donations</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }} sortDirection={sponsorsSort.field === 'pledge_amount' ? sponsorsSort.direction : false}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <TableSortLabel
                            active={sponsorsSort.field === 'pledge_amount'}
                            direction={sponsorsSort.field === 'pledge_amount' ? sponsorsSort.direction : 'asc'}
                            onClick={() => handleSponsorsSort('pledge_amount')}
                            sx={sponsorSortLabelSx}
                          >
                            Pledge amount
                          </TableSortLabel>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }} sortDirection={sponsorsSort.field === 'pledge_balance' ? sponsorsSort.direction : false}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <TableSortLabel
                            active={sponsorsSort.field === 'pledge_balance'}
                            direction={sponsorsSort.field === 'pledge_balance' ? sponsorsSort.direction : 'asc'}
                            onClick={() => handleSponsorsSort('pledge_balance')}
                            sx={sponsorSortLabelSx}
                          >
                            Pledge balance
                          </TableSortLabel>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Total revenue</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>% Pledge Paid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sponsorsFilteredSorted.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          {sponsorsRows.length === 0
                            ? 'No sponsors found.'
                            : sponsorsBalanceOnly
                              ? 'No sponsors with a pledge balance in this scope.'
                              : 'No sponsors match your search.'}
                        </TableCell>
                      </TableRow>
                    ) : sponsorsPageRows.map((s) => (
                      <React.Fragment key={s.sponsor_key}>
                        <TableRow sx={revenueTableRowHoverSx}>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleSponsorTx(s)}>
                              {expandedSponsorKey === s.sponsor_key ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {isGeneralDonationSponsor(s) ? '* ' : ''}
                            {s.sponsor_name || 'Guest'}
                          </TableCell>
                          <TableCell align="right">{Number(s.orders_count || 0).toLocaleString()}</TableCell>
                          <NonPledgeDonationCell metrics={s} />
                          <PledgeMetricCell applicable={hasPledgeMetrics(s)}>
                            {fmtCurrency(s.pledge_amount)}
                          </PledgeMetricCell>
                          <PledgeMetricCell applicable={hasPledgeMetrics(s)}>
                            {fmtCurrency(s.paid_amount)}
                          </PledgeMetricCell>
                          <PledgeMetricCell applicable={hasPledgeMetrics(s)}>
                            {fmtCurrency(s.pledge_balance)}
                          </PledgeMetricCell>
                          <TableCell align="right">{fmtCurrency(s.total_revenue)}</TableCell>
                          <PledgeMetricCell applicable={hasPledgeMetrics(s)}>
                            {fmtPercent(s.pct_pledge_paid)}
                          </PledgeMetricCell>
                        </TableRow>
                        {expandedSponsorKey === s.sponsor_key && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ bgcolor: 'action.hover' }}>
                              {txLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                  <CircularProgress size={22} />
                                </Box>
                              ) : txError ? (
                                <Alert severity="error">{txError}</Alert>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700 }}>Tx date</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>Non-pledge</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>Pledge amount</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {txRows.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>
                                          No transactions found.
                                        </TableCell>
                                      </TableRow>
                                    ) : txRows.map((tx) => (
                                      <TableRow key={`${tx.order_id}-${tx.product_id}-${tx.tx_date}`} sx={revenueTableRowHoverSx}>
                                        <TableCell>{fmtTxDate(tx.tx_date)}</TableCell>
                                        <TableCell>{tx.order_id}</TableCell>
                                        <TableCell
                                          sx={isPledgeStatus(tx.tx_status) ? { color: 'info.light', fontWeight: 500 } : undefined}
                                        >
                                          {tx.tx_status || '—'}
                                        </TableCell>
                                        <TableCell>{tx.product_name || `Product #${tx.product_id}`}</TableCell>
                                        <TableCell align="right">{fmtCurrency(tx.amount)}</TableCell>
                                        <NonPledgeDonationCell metrics={tx} />
                                        <PledgeMetricCell applicable={hasPledgeMetrics(tx)}>
                                          {fmtCurrency(tx.pledge_amount)}
                                        </PledgeMetricCell>
                                        <PledgeMetricCell applicable={hasPledgeMetrics(tx)}>
                                          {fmtCurrency(tx.paid_amount)}
                                        </PledgeMetricCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {sponsorsTablePagination}
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

